// =====================================================
// PodcastLibrary.js - Public Podcast Player for Users
// Beautiful tile-based UI for Learning Academy
// =====================================================

import React, { useState, useEffect, useRef } from 'react';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Get the full URL for audio files
const getAudioUrl = (audioUrl) => {
  if (!audioUrl) return null;
  // If it's already a full URL (http/https or data:), use as-is
  if (audioUrl.startsWith('http') || audioUrl.startsWith('data:')) return audioUrl;
  // If it's a relative URL starting with /api, construct full URL
  const baseUrl = API_BASE.replace(/\/api$/, '');
  return `${baseUrl}${audioUrl}`;
};

// Format duration from seconds to MM:SS
const formatDuration = (seconds) => {
  if (!seconds) return '--:--';
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

// Format date
const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// Category colors
const CATEGORY_COLORS = {
  'market-analysis': { bg: 'rgba(96, 165, 250, 0.15)', border: 'rgba(96, 165, 250, 0.3)', text: '#60a5fa' },
  'options': { bg: 'rgba(167, 139, 250, 0.15)', border: 'rgba(167, 139, 250, 0.3)', text: '#a78bfa' },
  'etfs': { bg: 'rgba(52, 211, 153, 0.15)', border: 'rgba(52, 211, 153, 0.3)', text: '#34d399' },
  'crypto': { bg: 'rgba(251, 191, 36, 0.15)', border: 'rgba(251, 191, 36, 0.3)', text: '#fbbf24' },
  'portfolio': { bg: 'rgba(129, 140, 248, 0.15)', border: 'rgba(129, 140, 248, 0.3)', text: '#818cf8' },
  'smart-money': { bg: 'rgba(34, 211, 238, 0.15)', border: 'rgba(34, 211, 238, 0.3)', text: '#22d3ee' },
  'fundamentals': { bg: 'rgba(244, 114, 182, 0.15)', border: 'rgba(244, 114, 182, 0.3)', text: '#f472b6' },
  'technical': { bg: 'rgba(251, 146, 60, 0.15)', border: 'rgba(251, 146, 60, 0.3)', text: '#fb923c' },
  'risk': { bg: 'rgba(248, 113, 113, 0.15)', border: 'rgba(248, 113, 113, 0.3)', text: '#f87171' },
  'psychology': { bg: 'rgba(192, 132, 252, 0.15)', border: 'rgba(192, 132, 252, 0.3)', text: '#c084fc' },
  'default': { bg: 'rgba(148, 163, 184, 0.15)', border: 'rgba(148, 163, 184, 0.3)', text: '#94a3b8' }
};

const getColor = (category) => CATEGORY_COLORS[category] || CATEGORY_COLORS.default;

// =====================================================
// AUDIO PLAYER COMPONENT
// =====================================================

const AudioPlayer = ({ episode, onClose, isMinimized, onToggleMinimize }) => {
  const audioRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [playbackRate, setPlaybackRate] = useState(1);

  useEffect(() => {
    if (audioRef.current && episode?.audio_url) {
      audioRef.current.load();
      audioRef.current.play().catch(() => {});
      setIsPlaying(true);
    }
  }, [episode?.episode_id]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSeek = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    if (audioRef.current) {
      audioRef.current.currentTime = percent * duration;
    }
  };

  const skip = (seconds) => {
    if (audioRef.current) {
      audioRef.current.currentTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
    }
  };

  const changeSpeed = () => {
    const speeds = [0.75, 1, 1.25, 1.5, 2];
    const currentIndex = speeds.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % speeds.length;
    const newRate = speeds[nextIndex];
    setPlaybackRate(newRate);
    if (audioRef.current) {
      audioRef.current.playbackRate = newRate;
    }
  };

  if (!episode) return null;

  const colors = getColor(episode.category);
  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  // Minimized player (sticky bottom bar)
  if (isMinimized) {
    return (
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
        borderTop: '1px solid rgba(255,255,255,0.1)',
        padding: '0.75rem 1rem',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        gap: '1rem'
      }}>
        <audio
          ref={audioRef}
          src={getAudioUrl(episode.audio_url)}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setIsPlaying(false)}
        />
        
        <button onClick={togglePlay} style={{
          width: '40px', height: '40px',
          borderRadius: '50%',
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          color: colors.text,
          cursor: 'pointer',
          fontSize: '1.25rem',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          {isPlaying ? '⏸' : '▶'}
        </button>
        
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {episode.title}
          </p>
          <div style={{
            height: '4px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '2px',
            marginTop: '0.5rem',
            cursor: 'pointer'
          }} onClick={handleSeek}>
            <div style={{
              height: '100%',
              width: `${progress}%`,
              background: colors.text,
              borderRadius: '2px',
              transition: 'width 0.1s'
            }} />
          </div>
        </div>
        
        <span style={{ fontSize: '0.75rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
          {formatDuration(Math.floor(currentTime))} / {formatDuration(Math.floor(duration))}
        </span>
        
        <button onClick={onToggleMinimize} style={{
          background: 'none',
          border: 'none',
          color: '#94a3b8',
          cursor: 'pointer',
          fontSize: '1.25rem'
        }}>⬆</button>
        
        <button onClick={onClose} style={{
          background: 'none',
          border: 'none',
          color: '#94a3b8',
          cursor: 'pointer',
          fontSize: '1.25rem'
        }}>✕</button>
      </div>
    );
  }

  // Full player (modal)
  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0,0,0,0.9)',
      zIndex: 1000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '1rem'
    }}>
      <div style={{
        width: '100%',
        maxWidth: '500px',
        background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
        borderRadius: '1.5rem',
        padding: '2rem',
        border: '1px solid rgba(255,255,255,0.1)'
      }}>
        <audio
          ref={audioRef}
          src={getAudioUrl(episode.audio_url)}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onEnded={() => setIsPlaying(false)}
        />
        
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <button onClick={onToggleMinimize} style={{
            background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.875rem'
          }}>⬇ Minimize</button>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '1.25rem'
          }}>✕</button>
        </div>
        
        {/* Album Art Placeholder */}
        <div style={{
          width: '200px',
          height: '200px',
          margin: '0 auto 1.5rem',
          borderRadius: '1rem',
          background: `linear-gradient(135deg, ${colors.bg}, ${colors.border})`,
          border: `2px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '4rem'
        }}>
          🎙️
        </div>
        
        {/* Title & Category */}
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.25rem', color: '#e2e8f0' }}>{episode.title}</h3>
          <span style={{
            display: 'inline-block',
            padding: '0.25rem 0.75rem',
            background: colors.bg,
            border: `1px solid ${colors.border}`,
            borderRadius: '1rem',
            fontSize: '0.75rem',
            color: colors.text
          }}>
            {episode.categoryIcon} {episode.categoryName || episode.category}
          </span>
        </div>
        
        {/* Progress Bar */}
        <div style={{ marginBottom: '1rem' }}>
          <div style={{
            height: '6px',
            background: 'rgba(255,255,255,0.1)',
            borderRadius: '3px',
            cursor: 'pointer',
            position: 'relative'
          }} onClick={handleSeek}>
            <div style={{
              height: '100%',
              width: `${progress}%`,
              background: `linear-gradient(90deg, ${colors.text}, ${colors.border})`,
              borderRadius: '3px',
              transition: 'width 0.1s'
            }} />
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', fontSize: '0.75rem', color: '#94a3b8' }}>
            <span>{formatDuration(Math.floor(currentTime))}</span>
            <span>{formatDuration(Math.floor(duration))}</span>
          </div>
        </div>
        
        {/* Controls */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem' }}>
          <button onClick={changeSpeed} style={{
            padding: '0.5rem 0.75rem',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '0.5rem',
            color: '#94a3b8',
            cursor: 'pointer',
            fontSize: '0.75rem'
          }}>
            {playbackRate}x
          </button>
          
          <button onClick={() => skip(-15)} style={{
            width: '48px', height: '48px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#e2e8f0',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}>
            ⏪15
          </button>
          
          <button onClick={togglePlay} style={{
            width: '64px', height: '64px',
            borderRadius: '50%',
            background: `linear-gradient(135deg, ${colors.text}, ${colors.border})`,
            border: 'none',
            color: '#fff',
            cursor: 'pointer',
            fontSize: '1.5rem',
            boxShadow: `0 4px 20px ${colors.bg}`
          }}>
            {isPlaying ? '⏸' : '▶'}
          </button>
          
          <button onClick={() => skip(30)} style={{
            width: '48px', height: '48px',
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#e2e8f0',
            cursor: 'pointer',
            fontSize: '0.875rem'
          }}>
            30⏩
          </button>
          
          <button style={{
            padding: '0.5rem 0.75rem',
            background: 'rgba(255,255,255,0.05)',
            border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '0.5rem',
            color: '#94a3b8',
            cursor: 'pointer',
            fontSize: '0.75rem'
          }}>
            🔊
          </button>
        </div>
        
        {/* Description */}
        {episode.description && (
          <p style={{
            marginTop: '1.5rem',
            padding: '1rem',
            background: 'rgba(255,255,255,0.03)',
            borderRadius: '0.75rem',
            fontSize: '0.875rem',
            color: '#94a3b8',
            lineHeight: '1.5'
          }}>
            {episode.description}
          </p>
        )}
      </div>
    </div>
  );
};

// =====================================================
// EPISODE TILE COMPONENT
// =====================================================

const EpisodeTile = ({ episode, onPlay, isPlaying }) => {
  const colors = getColor(episode.category);
  
  return (
    <div
      onClick={() => onPlay(episode)}
      style={{
        background: isPlaying 
          ? `linear-gradient(135deg, ${colors.bg}, rgba(15,23,42,0.8))`
          : 'rgba(30, 41, 59, 0.6)',
        border: `1px solid ${isPlaying ? colors.border : 'rgba(255,255,255,0.08)'}`,
        borderRadius: '1rem',
        padding: '1rem',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'flex',
        gap: '1rem',
        alignItems: 'center'
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = `0 8px 30px ${colors.bg}`;
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = 'none';
      }}
    >
      {/* Play Button / Thumbnail */}
      <div style={{
        width: '60px',
        height: '60px',
        borderRadius: '0.75rem',
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '1.5rem',
        flexShrink: 0
      }}>
        {isPlaying ? '🎧' : '▶️'}
      </div>
      
      {/* Content */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <h4 style={{
          margin: '0 0 0.25rem',
          fontSize: '0.95rem',
          fontWeight: '600',
          color: '#e2e8f0',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis'
        }}>
          {episode.title}
        </h4>
        
        <p style={{
          margin: '0 0 0.5rem',
          fontSize: '0.8rem',
          color: '#94a3b8',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {episode.description || 'Learn with Aryan from InvestorQ'}
        </p>
        
        <div style={{ display: 'flex', gap: '0.75rem', fontSize: '0.7rem', color: '#64748b' }}>
          <span>🎧 {formatDuration(episode.audio_duration_seconds)}</span>
          {episode.listen_count > 0 && <span>👁 {episode.listen_count}</span>}
          <span>{formatDate(episode.rss_published_at || episode.created_at)}</span>
        </div>
      </div>
    </div>
  );
};

// =====================================================
// CATEGORY SECTION COMPONENT
// =====================================================

const CategorySection = ({ category, episodes, onPlay, currentEpisodeId }) => {
  const colors = getColor(category.id);
  const [expanded, setExpanded] = useState(true);
  
  if (episodes.length === 0) return null;
  
  return (
    <div style={{ marginBottom: '2rem' }}>
      {/* Category Header */}
      <div 
        onClick={() => setExpanded(!expanded)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          marginBottom: '1rem',
          cursor: 'pointer'
        }}
      >
        <span style={{
          width: '40px',
          height: '40px',
          borderRadius: '0.75rem',
          background: colors.bg,
          border: `1px solid ${colors.border}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '1.25rem'
        }}>
          {category.icon}
        </span>
        
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, fontSize: '1.125rem', color: '#e2e8f0' }}>{category.name}</h3>
          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>{episodes.length} episode{episodes.length !== 1 ? 's' : ''}</span>
        </div>
        
        <span style={{ color: '#64748b', fontSize: '1.25rem' }}>
          {expanded ? '▼' : '▶'}
        </span>
      </div>
      
      {/* Episodes Grid */}
      {expanded && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: '1rem'
        }}>
          {episodes.map(ep => (
            <EpisodeTile
              key={ep.episode_id}
              episode={ep}
              onPlay={onPlay}
              isPlaying={currentEpisodeId === ep.episode_id}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// =====================================================
// MAIN PODCAST LIBRARY COMPONENT
// =====================================================

const PodcastLibrary = ({ moduleId = null, categoryFilter = null, compact = false }) => {
  const [library, setLibrary] = useState({ episodes: [], byCategory: [], categories: [] });
  const [loading, setLoading] = useState(true);
  const [currentEpisode, setCurrentEpisode] = useState(null);
  const [isMinimized, setIsMinimized] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(categoryFilter || 'all');

  useEffect(() => {
    loadLibrary();
  }, []);

  const loadLibrary = async () => {
    try {
      const response = await fetch(`${API_BASE}/podcast/library`);
      if (response.ok) {
        const data = await response.json();
        setLibrary(data);
      }
    } catch (error) {
      console.error('Error loading podcast library:', error);
    }
    setLoading(false);
  };

  const playEpisode = async (episode) => {
    // Fetch full episode details
    try {
      const response = await fetch(`${API_BASE}/podcast/library/${episode.episode_id}`);
      if (response.ok) {
        const data = await response.json();
        setCurrentEpisode(data.episode);
        setIsMinimized(false);
      }
    } catch (error) {
      // Fallback to basic episode data
      setCurrentEpisode(episode);
      setIsMinimized(false);
    }
  };

  const closePlayer = () => {
    setCurrentEpisode(null);
    setIsMinimized(false);
  };

  // Filter episodes
  const filteredCategories = library.byCategory.filter(cat => 
    selectedCategory === 'all' || cat.id === selectedCategory
  );

  // Get episode linked to specific module
  const linkedEpisode = moduleId 
    ? library.episodes.find(ep => ep.module_id === moduleId)
    : null;

  // Compact mode - just show linked episode for a module
  if (compact && linkedEpisode) {
    const colors = getColor(linkedEpisode.category);
    return (
      <div style={{
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: '1rem',
        padding: '1rem',
        marginBottom: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
          <span style={{ fontSize: '2rem' }}>🎙️</span>
          <div style={{ flex: 1 }}>
            <p style={{ margin: '0 0 0.25rem', fontSize: '0.875rem', fontWeight: '600', color: '#e2e8f0' }}>
              Listen to Podcast
            </p>
            <p style={{ margin: 0, fontSize: '0.75rem', color: '#94a3b8' }}>
              {linkedEpisode.title}
            </p>
          </div>
          <button
            onClick={() => playEpisode(linkedEpisode)}
            style={{
              padding: '0.5rem 1rem',
              background: colors.text,
              border: 'none',
              borderRadius: '0.5rem',
              color: '#fff',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '0.875rem'
            }}
          >
            ▶ Play
          </button>
        </div>
        
        {currentEpisode && (
          <AudioPlayer
            episode={currentEpisode}
            onClose={closePlayer}
            isMinimized={isMinimized}
            onToggleMinimize={() => setIsMinimized(!isMinimized)}
          />
        )}
      </div>
    );
  }

  // Compact mode but no linked episode
  if (compact && !linkedEpisode) {
    return null;
  }

  // Full library view
  return (
    <div style={{
      minHeight: '50vh',
      paddingBottom: currentEpisode && isMinimized ? '80px' : '0'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
          <h2 style={{ margin: 0, fontSize: '1.5rem', color: '#e2e8f0' }}>
            🎙️ Podcast Library
          </h2>
          <span style={{
            padding: '0.25rem 0.75rem',
            background: 'rgba(99, 102, 241, 0.2)',
            border: '1px solid rgba(99, 102, 241, 0.3)',
            borderRadius: '1rem',
            fontSize: '0.75rem',
            color: '#a5b4fc'
          }}>
            {library.episodes.length} Episodes
          </span>
        </div>
        
        <p style={{ margin: 0, color: '#94a3b8', fontSize: '0.9rem' }}>
          Learn investing with Aryan - your guide to becoming a more informed investor
        </p>
        
        {/* Category Filter */}
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button
            onClick={() => setSelectedCategory('all')}
            style={{
              padding: '0.5rem 1rem',
              background: selectedCategory === 'all' ? 'rgba(99, 102, 241, 0.3)' : 'rgba(255,255,255,0.05)',
              border: `1px solid ${selectedCategory === 'all' ? 'rgba(99, 102, 241, 0.5)' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: '2rem',
              color: selectedCategory === 'all' ? '#a5b4fc' : '#94a3b8',
              cursor: 'pointer',
              fontSize: '0.8rem'
            }}
          >
            All
          </button>
          {library.categories.map(cat => {
            const hasEpisodes = library.byCategory.some(c => c.id === cat.id && c.episodes.length > 0);
            if (!hasEpisodes) return null;
            
            const colors = getColor(cat.id);
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id)}
                style={{
                  padding: '0.5rem 1rem',
                  background: selectedCategory === cat.id ? colors.bg : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${selectedCategory === cat.id ? colors.border : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '2rem',
                  color: selectedCategory === cat.id ? colors.text : '#94a3b8',
                  cursor: 'pointer',
                  fontSize: '0.8rem'
                }}
              >
                {cat.icon} {cat.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8' }}>
          <p>Loading podcasts...</p>
        </div>
      )}

      {/* Empty State */}
      {!loading && library.episodes.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: 'rgba(30, 41, 59, 0.6)',
          borderRadius: '1rem',
          border: '1px solid rgba(255,255,255,0.08)'
        }}>
          <span style={{ fontSize: '3rem' }}>🎙️</span>
          <h3 style={{ margin: '1rem 0 0.5rem', color: '#e2e8f0' }}>Coming Soon!</h3>
          <p style={{ margin: 0, color: '#94a3b8' }}>
            Our podcast library is being built. Check back soon for educational content!
          </p>
        </div>
      )}

      {/* Episodes by Category */}
      {!loading && filteredCategories.map(cat => (
        <CategorySection
          key={cat.id}
          category={cat}
          episodes={cat.episodes}
          onPlay={playEpisode}
          currentEpisodeId={currentEpisode?.episode_id}
        />
      ))}

      {/* Audio Player */}
      {currentEpisode && (
        <AudioPlayer
          episode={currentEpisode}
          onClose={closePlayer}
          isMinimized={isMinimized}
          onToggleMinimize={() => setIsMinimized(!isMinimized)}
        />
      )}
    </div>
  );
};

export default PodcastLibrary;
